// ギフティング画面の UI プリミティブ（Step 0 で凍結）。スタイルは styles/gifting/gf-base.css。
// タブ・パネルはここの部品を組み合わせて作る（同等品をタブ側で再定義しない）。
import { useEffect, type ReactNode } from "react";
import type { GfStage } from "../../../types/gifting";
import { BAR_STAGES, initialOf } from "../../../lib/giftingRules";

export type GfTone = "ok" | "warn" | "caution" | "info" | "muted" | "plain";

export function GfCard({
  children,
  flush,
  className,
  onClick,
}: {
  children: ReactNode;
  flush?: boolean;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <div className={`gf-card${flush ? " is-flush" : ""}${className ? ` ${className}` : ""}`} onClick={onClick}>
      {children}
    </div>
  );
}

export function GfCardHead({ title, right, sub }: { title: ReactNode; right?: ReactNode; sub?: ReactNode }) {
  return (
    <div className="gf-card-head">
      <div>
        <div className="gf-h2">{title}</div>
        {sub && <div className="gf-note" style={{ marginTop: 2 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

export function GfTag({ tone = "plain", children }: { tone?: GfTone; children: ReactNode }) {
  return <span className={`gf-tag${tone !== "plain" ? ` is-${tone}` : ""}`}>{children}</span>;
}

export function GfMono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={`gf-mono${className ? ` ${className}` : ""}`}>{children}</span>;
}

export function GfAvatar({ name, size = 28 }: { name: string; size?: number }) {
  return (
    <span className="gf-avatar" style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }} aria-hidden>
      {initialOf(name)}
    </span>
  );
}

/** 7セグメント進捗バー（段階1〜7。到達済=primary）。height 既定5（待ちカードは4） */
export function GfStageBar({ stage, height = 5 }: { stage: GfStage; height?: number }) {
  return (
    <div className="gf-stagebar" aria-label={`段階 ${stage}/7`}>
      {BAR_STAGES.map((s) => (
        <span key={s} className={s <= stage ? "is-on" : ""} style={{ height }} />
      ))}
    </div>
  );
}

export interface GfSegOption<T extends string> {
  value: T;
  label: ReactNode;
}

export function GfSeg<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: GfSegOption<T>[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="gf-seg" role="tablist" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          className={`gf-seg-btn${o.value === value ? " is-active" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function GfChip({
  active,
  dark,
  onClick,
  children,
  sub,
}: {
  active?: boolean;
  /** 選択時に黒地白文字（素材庫のフィルタ） */
  dark?: boolean;
  onClick?: () => void;
  children: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`gf-chip${active ? " is-active" : ""}${dark ? " is-dark" : ""}`}
      onClick={onClick}
      aria-pressed={active}
    >
      {children}
      {sub !== undefined && <span className="gf-chip-sub">{sub}</span>}
    </button>
  );
}

export function GfButton({
  variant = "secondary",
  size = "md",
  block,
  disabled,
  onClick,
  type = "button",
  children,
  title,
}: {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  block?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: "button" | "submit";
  children: ReactNode;
  title?: string;
}) {
  const cls = [
    "gf-btn",
    variant !== "secondary" ? `is-${variant}` : "",
    size !== "md" ? `is-${size}` : "",
    block ? "is-block" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button type={type} className={cls} disabled={disabled} onClick={onClick} title={title}>
      {children}
    </button>
  );
}

export function GfField({
  label,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="gf-field">
      <span className="gf-field-label">{label}</span>
      {children}
      {error ? <span className="gf-field-error">{error}</span> : hint ? <span className="gf-field-hint">{hint}</span> : null}
    </label>
  );
}

export function GfSkeleton({ height = 16, width = "100%" }: { height?: number; width?: number | string }) {
  return <div className="gf-skel" style={{ height, width }} aria-hidden />;
}

export function GfEmpty({ children }: { children: ReactNode }) {
  return <div className="gf-empty">{children}</div>;
}

/** 未接続の機能。サンプル値で埋めず、何がいつ繋がるかを正直に出す */
export function GfNotConnected({ title, phase, note }: { title: ReactNode; phase?: string; note?: ReactNode }) {
  return (
    <div className="gf-notconn">
      <div className="gf-notconn-title">
        {title}
        <GfTag tone="muted">未接続{phase ? `・${phase}で接続予定` : ""}</GfTag>
      </div>
      {note && <div className="gf-notconn-note">{note}</div>}
    </div>
  );
}

export function GfErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="gf-banner is-error" role="alert">
      <span>{message}</span>
      {onRetry && (
        <GfButton size="sm" onClick={onRetry}>
          再試行
        </GfButton>
      )}
    </div>
  );
}

/** 汎用モーダル（Escape・背景クリックで閉じる）。z-index 50 */
export function GfModal({
  open,
  title,
  onClose,
  children,
  footer,
  width,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="gf-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="gf-modal" role="dialog" aria-modal="true" style={width ? { width } : undefined}>
        <div className="gf-modal-head">
          <div className="gf-h2">{title}</div>
          <button type="button" className="gf-panel-close" style={{ position: "static" }} onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </div>
        {children}
        {footer && <div className="gf-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * 右詳細パネルの外枠（固定オーバーレイ・Escape で閉じる・スマホは全画面シート＋body スクロールロック）。
 * 中身は EntryPanel / CreatorPanel が組む。
 */
export function GfPanelFrame({ onClose, children, label }: { onClose: () => void; children: ReactNode; label: string }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const mobile = window.matchMedia("(max-width: 768px)").matches;
    const prev = document.body.style.overflow;
    if (mobile) document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      if (mobile) document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <aside className="gf-panel" role="dialog" aria-label={label}>
      <button type="button" className="gf-panel-close" onClick={onClose} aria-label="閉じる">
        ×
      </button>
      {children}
    </aside>
  );
}

export function GfFacts({ rows }: { rows: Array<{ k: ReactNode; v: ReactNode; mono?: boolean }> }) {
  return (
    <div className="gf-facts">
      {rows.map((r, i) => (
        <div className="gf-fact" key={i}>
          <span className="gf-fact-k">{r.k}</span>
          <span className={`gf-fact-v${r.mono ? " gf-mono" : ""}`}>{r.v}</span>
        </div>
      ))}
    </div>
  );
}

/** 横棒（段階別件数・チャネル売上）。pct は 0〜100 */
export function GfHBar({ pct, sub }: { pct: number; sub?: boolean }) {
  return (
    <div className={`gf-hbar${sub ? " is-sub" : ""}`}>
      <span style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

/** クリップボードへコピー（失敗時は false） */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Blob を保存させる（CSV 出力など） */
export function downloadText(filename: string, text: string, mime = "text/csv;charset=utf-8"): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
